import { sessionService } from '@/shared/services/session.service';

/**
 * Utilidades para manejo de autenticación
 */

export interface AuthError {
  status: number;
  message: string;
  shouldRedirect: boolean;
  shouldShowError: boolean;
}

/**
 * Analiza un error HTTP y determina cómo manejarlo
 */
export const analyzeAuthError = (error: any): AuthError => {
  const status = error.response?.status || 0;
  const url = error.config?.url || '';
  const method = error.config?.method?.toUpperCase() || '';
  const currentPath = window.location.pathname;

  // Error 401 - No autorizado
  if (status === 401) {
    const isAuthPath =
      currentPath.includes('/login') || currentPath.includes('/auth');
    const isLoginEndpoint = url.includes('/auth/login');
    const isFormSubmission =
      method &&
      ['POST', 'PUT', 'PATCH'].includes(method) &&
      error.config?.headers?.['Content-Type']?.includes('application/json');

    // Si es el endpoint de login, no redirigir (permitir manejo de errores del formulario)
    if (isLoginEndpoint) {
      return {
        status,
        message: 'Credenciales inválidas',
        shouldRedirect: false,
        shouldShowError: true,
      };
    }

    // Si es una página de auth, no redirigir
    if (isAuthPath) {
      return {
        status,
        message: 'Sesión expirada',
        shouldRedirect: false,
        shouldShowError: true,
      };
    }

    // Si es un formulario, pero el token expiró, redirigir al login
    if (isFormSubmission) {
      return {
        status,
        message: 'Tu sesión ha expirado. Por favor, inicia sesión nuevamente.',
        shouldRedirect: true,
        shouldShowError: false,
      };
    }

    // Para otros casos, redirigir al login
    return {
      status,
      message: 'Sesión expirada. Redirigiendo al login...',
      shouldRedirect: true,
      shouldShowError: false,
    };
  }

  // Error 403 - Prohibido
  if (status === 403) {
    return {
      status,
      message: 'No tienes permisos para realizar esta acción',
      shouldRedirect: false,
      shouldShowError: true,
    };
  }

  // Error 404 - No encontrado
  if (status === 404) {
    return {
      status,
      message: 'Recurso no encontrado',
      shouldRedirect: false,
      shouldShowError: true,
    };
  }

  // Error 409 - Conflicto
  if (status === 409) {
    return {
      status,
      message: 'Conflicto: El recurso ya existe',
      shouldRedirect: false,
      shouldShowError: true,
    };
  }

  // Error 422 - Validación
  if (status === 422) {
    return {
      status,
      message: 'Datos de entrada inválidos',
      shouldRedirect: false,
      shouldShowError: true,
    };
  }

  // Error 500 - Servidor
  if (status >= 500) {
    return {
      status,
      message: 'Error interno del servidor',
      shouldRedirect: false,
      shouldShowError: true,
    };
  }

  // Error genérico
  return {
    status,
    message:
      error.response?.data?.message || error.message || 'Error desconocido',
    shouldRedirect: false,
    shouldShowError: true,
  };
};

/**
 * Limpia los datos de autenticación del localStorage
 */
export const clearAuthData = () => {
  sessionService.clearSession();
};

/**
 * Verifica si el usuario está autenticado
 */
export const isAuthenticated = (): boolean => {
  const hasAuthCookie = document.cookie.includes('auth_token=');
  const hasActiveSession = sessionService.hasActiveSession();
  return hasAuthCookie || hasActiveSession;
};
